import { type Doc } from "building-types/doc";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { RWTable } from "common/data/table";
import { docEntityForEachChild } from "./docEntityForEachChild";

export type DocEntityGetChildrenParams = Parameters<typeof docEntityGetChildren>;

/**
 * Get all children of an entity. This variant omits the children that are part of
 * a group and returns the group instead. So, it looks like the output in studio.
 *
 * @param doc - The document to search in.
 * @param parentHandle - The parent entity to get children for.
 * @param recursive - If true, will also get children of children, and so on.
 * @param visited - Used to ensure the children are unique, but can also be used to skip entities.
 * @returns An array of all children handles for the parent entity.
 */
export default function docEntityGetChildren(
  doc: Doc,
  parentHandle: DocEntityHandle,
  recursive: boolean = false,
  visited: RWTable<boolean> = {},
): DocEntityHandle[] {
  return docEntityGetChildrenInternal(doc, parentHandle, recursive, false, visited);
}

/**
 * Get all children of an entity. This variant ignores groups that make up children,
 * but the children that are part of a group are still returned in the result.
 *
 * @param doc - The document to search in.
 * @param parentHandle - The parent entity to get children for.
 * @param recursive - If true, will also get children of children, and so on.
 * @param visited - Used to ensure the children are unique, but can also be used to skip entities.
 * @returns An array of all children handles for the parent entity.
 */
export function docEntityGetChildrenIgnoringGroups(
  doc: Doc,
  parentHandle: DocEntityHandle,
  recursive: boolean = false,
  visited: RWTable<boolean> = {},
): DocEntityHandle[] {
  return docEntityGetChildrenInternal(doc, parentHandle, recursive, true, visited);
}

function docEntityGetChildrenInternal(
  doc: Doc,
  parentHandle: DocEntityHandle,
  recursive: boolean,
  ignoreGroups: boolean,
  visited: RWTable<boolean>,
): DocEntityHandle[] {
  const children: DocEntityHandle[] = [];

  docEntityForEachChild(doc, parentHandle, (handle) => children.push(handle), recursive, ignoreGroups, visited);

  return children;
}
